import { getCurrencyInfo } from '@transferz/utils';
import { useMemo } from 'react';

import { useLocalize } from '../../hooks/useLocalize';
import { useLocalPreferredCurrencyCode } from '../../hooks/useLocalPreferredCurrencyCode';
import { useQueryCurrencies } from '../../queries/currency/hooks/useQueryCurrencies';

export interface ICurrency {
  key: string;
  value: string;
}

const suggestedCurrenciesArr = ['EUR', 'USD'];
const useSuggestedCurrencies = () => {
  const { translate: t } = useLocalize();
  const suggestedCurrencies = useMemo(
    () => {
      try {
        const suggested = t('suggested_currencies_arr');
        return JSON.parse(suggested) as string[];
      } catch (e) {
        return suggestedCurrenciesArr;
      }
    }
    , [t('suggested_currencies')]);
  return suggestedCurrencies;
};

const getCurrencyByCode = (currencyCode: string): ICurrency => {
  return { key: currencyCode, value: getCurrencyInfo(currencyCode).currency };
};

const compareCurrencies = (a: ICurrency, b: ICurrency): number => a.key.localeCompare(b.key);

export const useCurrencies = () => {
  const [preferredCurrencyCode,, regionCurrency] = useLocalPreferredCurrencyCode() as [string, any, string];
  const { data: currencyData, loading } = useQueryCurrencies();
  const suggestedCurrenciesArr = useSuggestedCurrencies();

  /*
   * Suggested currencies plus the region currency (as the first one),
   * except the preferred currency
   */
  const suggestedCurrencies = useMemo(
    () => [
      ...((regionCurrency && regionCurrency !== preferredCurrencyCode) ? [getCurrencyByCode(regionCurrency)] : []),
      ...suggestedCurrenciesArr
        .filter((item) => ![regionCurrency, preferredCurrencyCode].includes(item))
        .map(getCurrencyByCode)
        .sort(compareCurrencies),
    ],
    [regionCurrency, preferredCurrencyCode, suggestedCurrenciesArr],
  );

  /*
   * All currencies except the suggested ones.
   * The preferred currency should appear first
   */
  const otherCurrencies = useMemo(
    () =>{
      const suggestedCurrencyKeys = suggestedCurrencies.map((currency) => currency.key);
      return [
        getCurrencyByCode(preferredCurrencyCode),
        ...(currencyData?.currencies as string[] || [])
          .filter(currency => !suggestedCurrencyKeys.includes(currency) && currency !== preferredCurrencyCode)
          .map(getCurrencyByCode)
          .sort(compareCurrencies)];
    },
    [currencyData, preferredCurrencyCode, regionCurrency, suggestedCurrenciesArr],
  );

  return {
    loading,
    suggestedCurrencies,
    otherCurrencies,
  };
};
